import type { OccurrenceDto } from '@familynest/shared'
import { addDays, startOfDay } from '../../lib/date'

const MS_DAY = 86_400_000

/** Lokaler Tages-Schlüssel 'YYYY-MM-DD' für Gruppierung. */
export function localDayKey(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Tages-Spanne eines Vorkommnisses (lokale Tage, inklusive Anfang und Ende).
 * Bei zeitgebundenen Terminen, die exakt um Mitternacht enden, zählt der
 * Endtag NICHT mit (sonst ragt ein Termin einen Tag zu weit). Ganztägige
 * Termine bleiben inklusiv, damit der letzte Tag mitgezählt wird.
 */
export function occurrenceDayRange(o: OccurrenceDto): { startDay: Date; endDay: Date } {
  const start = new Date(o.occurrenceStart)
  const end = new Date(o.occurrenceEnd)
  const startDay = startOfDay(start)
  let endDay = startOfDay(end)
  if (!o.allDay && end.getTime() === endDay.getTime() && endDay.getTime() > startDay.getTime()) {
    endDay = addDays(endDay, -1)
  }
  if (endDay.getTime() < startDay.getTime()) endDay = startDay
  return { startDay, endDay }
}

/** Läuft ein Termin über mehr als einen Kalendertag? */
export function isMultiDay(o: OccurrenceDto): boolean {
  const { startDay, endDay } = occurrenceDayRange(o)
  return endDay.getTime() > startDay.getTime()
}

/** Tages-Index (0–6) eines Tages relativ zum Wochenanfang. */
function dayIndex(weekStart: Date, day: Date): number {
  return Math.round((startOfDay(day).getTime() - weekStart.getTime()) / MS_DAY)
}

/** Ein positionierter Balken innerhalb einer Kalenderwoche. */
export type WeekSegment = {
  occ: OccurrenceDto
  lane: number
  colStart: number // 0–6
  colSpan: number // 1–7
  continuesLeft: boolean // beginnt vor dieser Woche
  continuesRight: boolean // endet nach dieser Woche
}

/**
 * Layout einer Kalenderwoche: jedes Vorkommnis wird zu einem horizontalen
 * Balken, der über alle betroffenen Tage der Woche reicht. Balken werden
 * überschneidungsfrei auf „Lanes" (Zeilen) verteilt — mehrtägige Termine
 * erscheinen so als durchgehender Balken, einzelne als 1-Tages-Balken.
 */
export function layoutWeek(week: Date[], occ: OccurrenceDto[]): WeekSegment[] {
  const weekStart = week[0]!
  const weekEnd = week[6]!
  const raw: Omit<WeekSegment, 'lane'>[] = []
  for (const o of occ) {
    const { startDay, endDay } = occurrenceDayRange(o)
    if (endDay.getTime() < weekStart.getTime() || startDay.getTime() > weekEnd.getTime()) continue
    const segStart = startDay.getTime() < weekStart.getTime() ? weekStart : startDay
    const segEnd = endDay.getTime() > weekEnd.getTime() ? weekEnd : endDay
    const colStart = dayIndex(weekStart, segStart)
    const colEnd = dayIndex(weekStart, segEnd)
    raw.push({
      occ: o,
      colStart,
      colSpan: colEnd - colStart + 1,
      continuesLeft: startDay.getTime() < weekStart.getTime(),
      continuesRight: endDay.getTime() > weekEnd.getTime(),
    })
  }
  // Früher Start zuerst, längere Balken zuerst → ruhiges „Treppen"-Layout.
  raw.sort(
    (a, b) =>
      a.colStart - b.colStart ||
      b.colSpan - a.colSpan ||
      a.occ.occurrenceStart.localeCompare(b.occ.occurrenceStart),
  )
  // Greedy-Lanes: erste Lane, deren letzter belegter Tag vor dem Balken liegt.
  const laneEnds: number[] = []
  const segs: WeekSegment[] = []
  for (const r of raw) {
    let lane = 0
    while (lane < laneEnds.length && laneEnds[lane]! >= r.colStart) lane++
    laneEnds[lane] = r.colStart + r.colSpan - 1
    segs.push({ ...r, lane })
  }
  return segs
}

/**
 * Vorkommnisse auf sichtbare Tage verteilen — mehrtägige Termine erscheinen an
 * JEDEM betroffenen Tag. Je Tag sortiert: ganz-/mehrtägige zuerst, dann nach
 * Startzeit. Für Agenda-Ansichten (Woche/Tag).
 */
export function groupByDaySpanning(occ: OccurrenceDto[], days: Date[]): Map<string, OccurrenceDto[]> {
  const map = new Map<string, OccurrenceDto[]>()
  for (const day of days) map.set(localDayKey(day), [])
  for (const o of occ) {
    const { startDay, endDay } = occurrenceDayRange(o)
    for (const day of days) {
      const t = startOfDay(day).getTime()
      if (t < startDay.getTime() || t > endDay.getTime()) continue
      map.get(localDayKey(day))!.push(o)
    }
  }
  for (const list of map.values()) {
    list.sort((a, b) => {
      const am = isMultiDay(a) || a.allDay ? 0 : 1
      const bm = isMultiDay(b) || b.allDay ? 0 : 1
      return am - bm || a.occurrenceStart.localeCompare(b.occurrenceStart)
    })
  }
  return map
}

/** Vorkommnisse nach lokalem Tag gruppieren, je Tag nach Startzeit sortiert. */
export function groupByDay(occ: OccurrenceDto[]): Map<string, OccurrenceDto[]> {
  const map = new Map<string, OccurrenceDto[]>()
  for (const o of occ) {
    const key = localDayKey(new Date(o.occurrenceStart))
    const list = map.get(key) ?? []
    list.push(o)
    map.set(key, list)
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.occurrenceStart.localeCompare(b.occurrenceStart))
  }
  return map
}
